import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Building2, Mail, Lock, Eye, EyeOff, User as UserIcon, Phone, AlertCircle, CheckCircle } from 'lucide-react';

export default function Register() {
  const { setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [showPwd, setShowPwd]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      return setError(t('nameEmailPasswordRequired'));
    }
    if (form.password.length < 6) {
      return setError(t('passwordTooShort'));
    }
    if (form.password !== form.confirmPassword) {
      return setError(t('passwordsDoNotMatch'));
    }
    setLoading(true);
    try {
      const { data } = await authAPI.register({
        name:     form.name.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim() || undefined,
        password: form.password,
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.message || t('registrationFailed'));
    } finally { setLoading(false); }
  };

  const pwdStrength = p => {
    if (!p) return null;
    if (p.length < 6)  return { level: 0, label: t('tooShort'),  color: 'var(--rose)' };
    if (p.length < 8)  return { level: 1, label: t('weak'),      color: 'var(--amber)' };
    if (/[A-Z]/.test(p) && /[0-9]/.test(p)) return { level: 3, label: t('strong'), color: 'var(--emerald)' };
    return { level: 2, label: t('fair'), color: 'var(--brand)' };
  };
  const strength = pwdStrength(form.password);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--ink)' }}>
      {/* Left branding panel — hidden on mobile */}
      <div className="mobile-hide" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, background: 'linear-gradient(135deg,#0a0f1e 0%,#1a2650 100%)' }}>
        <Building2 size={52} color="var(--brand-light)" style={{ marginBottom: 22 }} />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 38, color: '#fff', textAlign: 'center', lineHeight: 1.2, marginBottom: 14 }}>PropManager</h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 300, lineHeight: 1.7, fontSize: 15, marginBottom: 36 }}>
          {t('registerLeftPanelSubtitle')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 320 }}>
          {[
            [t('viewYourLease'),        t('viewYourLeaseDesc')],
            [t('submitWorkOrders'),     t('submitWorkOrdersDesc')],
            [t('trackPayments'),        t('trackPaymentsDesc')],
            [t('accessDocuments'),      t('accessDocumentsDesc')],
          ].map(([title, desc]) => (
            <div key={title} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '13px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <CheckCircle size={16} color="var(--brand-light)" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-light)', marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', background: 'var(--white)', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          {/* Logo + title */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={20} color="#fff" />
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>PropManager</span>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{t('createTenantAccount')}</h2>
            <p style={{ color: 'var(--ink-40)', fontSize: 14 }}>{t('registerSubtitle')}</p>
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 18 }}>
              <AlertCircle size={16} />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Full name */}
            <div className="form-group">
              <label className="form-label">{t('fullName')} *</label>
              <div style={{ position: 'relative' }}>
                <UserIcon size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type="text" required autoComplete="name"
                  placeholder={t('enterFullName')} value={form.name} onChange={set('name')}
                  style={{ paddingLeft: 38 }} />
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label className="form-label">{t('email')} *</label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type="email" required autoComplete="email"
                  placeholder={t('enterEmail')} value={form.email} onChange={set('email')}
                  style={{ paddingLeft: 38 }} />
              </div>
            </div>

            {/* Phone */}
            <div className="form-group">
              <label className="form-label">{t('phone')} <span style={{ fontWeight: 400, color: 'var(--ink-20)' }}>({t('optional')})</span></label>
              <div style={{ position: 'relative' }}>
                <Phone size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type="tel" autoComplete="tel"
                  placeholder="+1 (555) 000-0000" value={form.phone} onChange={set('phone')}
                  style={{ paddingLeft: 38 }} />
              </div>
            </div>

            {/* Password */}
            <div className="form-group">
              <label className="form-label">{t('password')} *</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type={showPwd ? 'text' : 'password'} required autoComplete="new-password"
                  placeholder={t('min6Characters')} value={form.password} onChange={set('password')}
                  style={{ paddingLeft: 38, paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-40)', padding: '0 12px', height: '100%', display: 'flex', alignItems: 'center', minWidth: 44 }}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Strength indicator */}
              {strength && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 9999, background: i <= strength.level - 1 ? strength.color : 'var(--border)', transition: 'background .2s' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: strength.color, fontWeight: 600 }}>{strength.label}</span>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="form-group">
              <label className="form-label">{t('confirmPassword')} *</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type={showConfirm ? 'text' : 'password'} required autoComplete="new-password"
                  placeholder={t('repeatPassword')} value={form.confirmPassword} onChange={set('confirmPassword')}
                  style={{ paddingLeft: 38, paddingRight: 42, borderColor: form.confirmPassword && form.password !== form.confirmPassword ? 'var(--rose)' : undefined }} />
                <button type="button" onClick={() => setShowConfirm(s => !s)}
                  style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-40)', padding: '0 12px', height: '100%', display: 'flex', alignItems: 'center', minWidth: 44 }}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <span style={{ fontSize: 11, color: 'var(--rose)', fontWeight: 600, marginTop: 2 }}>{t('passwordsDoNotMatch')}</span>
              )}
            </div>

            <button className="btn btn-primary w-full" type="submit" disabled={loading}
              style={{ justifyContent: 'center', padding: '12px', marginTop: 4, fontSize: 15 }}>
              {loading
                ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                : t('createAccount')}
            </button>
          </form>

          {/* Sign in link */}
          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 14, color: 'var(--ink-40)' }}>
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" style={{ color: 'var(--brand)', fontWeight: 600 }}>{t('signIn')}</Link>
          </p>

          {/* Language switcher */}
          <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', justifyContent: 'center', gap: 12 }}>
            {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
              <button key={code} onClick={() => i18n.changeLanguage(code)}
                style={{ fontSize: 12, color: i18n.language === code ? 'var(--brand)' : 'var(--ink-20)', fontWeight: i18n.language === code ? 700 : 400, background: 'none', border: 'none', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
